import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, PageHeader } from "@/components/Layout";
import { useClient } from "@/context/ClientContext";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Building2, Boxes, ShieldCheck, Cpu, CheckCircle2, ArrowRight, ArrowLeft,
  Loader2, Plus, Trash2, ClipboardList, FileText, Sparkles, Copy, Info, BookMarked,
} from "lucide-react";

const SECTORS = ["Manifatturiero", "Sanità", "Finance / Assicurazioni", "Pubblica Amministrazione", "Retail / GDO", "Energia & Utilities", "Trasporti & Logistica", "Servizi professionali", "IT & Software", "Costruzioni", "Alimentare", "Farmaceutico", "Altro"];

const STEPS = [
  { n: 1, label: "Azienda & Reparti", icon: Building2 },
  { n: 2, label: "Stack IT", icon: Boxes },
  { n: 3, label: "Readiness", icon: Cpu },
  { n: 4, label: "Cyber & Compliance", icon: ShieldCheck },
  { n: 5, label: "Riepilogo", icon: CheckCircle2 },
];
const IT_CATEGORIES = ["ERP / Gestionale", "CRM", "Email & Collaboration", "Backup & DR", "Rete & Firewall", "Cloud / Hosting", "Endpoint / Antivirus", "Identità & Accessi", "Storage", "Altro"];
const ANSWER_OPTS = [
  { v: "yes", label: "Sì", cls: "bg-[#00A859] text-white border-[#00A859]" },
  { v: "partial", label: "Parziale", cls: "bg-[#F59E0B] text-white border-[#F59E0B]" },
  { v: "no", label: "No", cls: "bg-destructive text-white border-destructive" },
];
const emptyDept = () => ({ headcount: "", staff_age_avg: "", infra_age_avg: "", last_refresh_year: "" });

function DeptRow({ label, value, onChange }) {
  const f = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid grid-cols-5 gap-2 items-center">
      <div className="text-sm font-medium">{label}</div>
      <Input className="rounded-none h-9" type="number" placeholder="Addetti" value={value.headcount} onChange={f("headcount")} data-testid={`dept-${label}-headcount`} />
      <Input className="rounded-none h-9" type="number" placeholder="Età pers." value={value.staff_age_avg} onChange={f("staff_age_avg")} />
      <Input className="rounded-none h-9" type="number" placeholder="Età infra (anni)" value={value.infra_age_avg} onChange={f("infra_age_avg")} />
      <Input className="rounded-none h-9" type="number" placeholder="Ultimo rinnovo" value={value.last_refresh_year} onChange={f("last_refresh_year")} />
    </div>
  );
}

export default function Wizard() {
  const navigate = useNavigate();
  const { loadClients, setActiveClient } = useClient();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [clientId, setClientId] = useState(null);

  // Step 1
  const [company, setCompany] = useState({ name: "", sector: "", location: "", employees_total: "", ot_present: false, ot_notes: "", notes: "" });
  const [deptIt, setDeptIt] = useState(emptyDept());
  const [deptOt, setDeptOt] = useState(emptyDept());
  const [deptSec, setDeptSec] = useState(emptyDept());
  // Step 2
  const [tools, setTools] = useState([{ category: "ERP / Gestionale", name: "", vendor: "", notes: "" }]);
  // Step 3
  const [assessmentId, setAssessmentId] = useState(null);
  const [areas, setAreas] = useState([]);
  const [scores, setScores] = useState({});
  // Step 4
  const [controls, setControls] = useState([]);
  const [answers, setAnswers] = useState({});
  // AI
  const [stackBusy, setStackBusy] = useState(false);
  const [stackSuggestions, setStackSuggestions] = useState(null);
  const [execBusy, setExecBusy] = useState(false);
  const [execSummary, setExecSummary] = useState(null);

  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const suggestStack = async () => {
    if (!clientId) { toast.error("Salva prima lo step Azienda"); return; }
    setStackBusy(true);
    try {
      const existing = tools.filter((t) => t.name.trim()).map((t) => t.name.trim());
      const { data } = await api.post(`/clients/${clientId}/ai-stack-suggest`, { existing });
      setStackSuggestions(data.suggestions || []);
      if (!(data.suggestions || []).length) toast.info("Nessun suggerimento aggiuntivo");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Suggerimento AI non riuscito"); }
    finally { setStackBusy(false); }
  };

  const addSuggestion = (s) => {
    setTools((prev) => [...prev, { category: s.category || "Altro", name: s.tool || "", vendor: s.vendor || "", notes: s.why || "" }]);
    setStackSuggestions((prev) => (prev || []).filter((x) => x !== s));
    toast.success("Tool aggiunto");
  };

  const generateExecSummary = async () => {
    setExecBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/ai-exec-summary`);
      setExecSummary(data);
      toast.success("Riepilogo esecutivo generato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generazione non riuscita"); }
    finally { setExecBusy(false); }
  };

  const copyExecSummary = () => {
    if (!execSummary) return;
    const prios = (execSummary.priorities || []).map((p, i) => `${i + 1}. ${p.title}${p.detail ? " — " + p.detail : ""}`).join("\n");
    navigator.clipboard.writeText(`${execSummary.summary || ""}\n\nPriorità:\n${prios}`);
    toast.success("Riepilogo copiato negli appunti");
  };

  const saveStep1 = async () => {
    if (!company.name.trim()) { toast.error("Nome cliente obbligatorio"); return false; }
    let cid = clientId;
    if (!cid) {
      const { data } = await api.post("/clients", { name: company.name, sector: company.sector, notes: company.notes });
      cid = data.id; setClientId(cid);
    } else {
      await api.put(`/clients/${cid}`, { name: company.name, sector: company.sector, notes: company.notes });
    }
    const dept = (d) => ({ headcount: num(d.headcount) || 0, staff_age_avg: num(d.staff_age_avg), infra_age_avg: num(d.infra_age_avg), last_refresh_year: num(d.last_refresh_year) });
    await api.put(`/clients/${cid}/profile`, {
      location: company.location, employees_total: num(company.employees_total) || 0,
      dept_it: dept(deptIt), dept_ot: dept(deptOt), dept_security: dept(deptSec),
      ot_present: company.ot_present, ot_notes: company.ot_notes, notes: company.notes,
    });
    return true;
  };

  const saveStep2 = async () => {
    const valid = tools.filter((t) => t.name.trim());
    for (const t of valid) {
      await api.post("/tools", { name: t.name, vendor: t.vendor, category: t.category, notes: t.notes, client_id: clientId, scores: {} });
    }
    return true;
  };

  const initStep3 = async () => {
    if (assessmentId) return;
    const { data } = await api.post(`/clients/${clientId}/assessments`);
    setAssessmentId(data.id);
    const ar = (data.breakdown || []).map((b) => ({ id: b.key, name: b.name }));
    setAreas(ar);
    setScores(Object.fromEntries(ar.map((a) => [a.id, 3])));
  };

  const saveStep3 = async () => {
    if (assessmentId) {
      await api.put(`/readiness/${assessmentId}`, { client_name: company.name, sector: company.sector, framework_id: "default", scores });
    }
    return true;
  };

  const initStep4 = async () => {
    const { data } = await api.get(`/compliance/assessment/${clientId}`);
    setControls(data.controls || []);
    setAnswers(data.answers || {});
  };

  const saveStep4 = async () => {
    await api.put(`/compliance/assessment/${clientId}`, { answers });
    return true;
  };

  const next = async () => {
    setBusy(true);
    try {
      let ok = true;
      if (step === 1) ok = await saveStep1();
      else if (step === 2) ok = await saveStep2();
      else if (step === 3) ok = await saveStep3();
      else if (step === 4) ok = await saveStep4();
      if (!ok) { setBusy(false); return; }
      const nextStep = step + 1;
      if (nextStep === 3) await initStep3();
      if (nextStep === 4) await initStep4();
      setStep(nextStep);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore, riprova");
    } finally { setBusy(false); }
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  const finish = async () => {
    try {
      await api.post(`/clients/${clientId}/surveys`);
    } catch (e) { /* snapshot non bloccante */ }
    await loadClients();
    setActiveClient(clientId);
    toast.success("Sopralluogo salvato");
    navigate(`/clients/${clientId}`);
  };

  const fwGroups = ["NIS2", "DORA", "GDPR", "OT"].map((fw) => ({ fw, items: controls.filter((c) => c.framework === fw) }));

  return (
    <Layout>
      <PageHeader title="Sopralluogo cliente" subtitle="Raccolta guidata delle informazioni">
        <Button variant="outline" className="rounded-none" onClick={() => navigate("/clients")}>Esci</Button>
      </PageHeader>

      <div className="p-4 sm:p-8 max-w-4xl">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="wizard-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = step > s.n, active = step === s.n;
            return (
              <div key={s.n} className="flex items-center gap-2 shrink-0">
                <div className={`flex items-center gap-2 px-3 py-2 border ${active ? "border-primary bg-primary text-primary-foreground" : done ? "border-[#00A859] text-[#00784A]" : "border-border text-muted-foreground"}`}>
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase tracking-wider hidden sm:inline">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`w-4 h-px ${done ? "bg-[#00A859]" : "bg-border"}`} />}
              </div>
            );
          })}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-5" data-testid="wizard-step-1">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="data-label">Nome cliente *</Label><Input data-testid="wiz-client-name" className="mt-1 rounded-none" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} /></div>
              <div>
                <Label className="data-label">Settore</Label>
                <select data-testid="wiz-client-sector" className="mt-1 w-full h-10 border border-border rounded-none text-sm px-3 bg-white" value={company.sector} onChange={(e) => setCompany({ ...company, sector: e.target.value })}>
                  <option value="">— Seleziona settore —</option>
                  {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><Label className="data-label">Sede</Label><Input className="mt-1 rounded-none" value={company.location} onChange={(e) => setCompany({ ...company, location: e.target.value })} /></div>
              <div><Label className="data-label">Dipendenti totali</Label><Input type="number" className="mt-1 rounded-none" value={company.employees_total} onChange={(e) => setCompany({ ...company, employees_total: e.target.value })} /></div>
            </div>
            <div>
              <Label className="data-label">Reparti (dimensione ed età)</Label>
              <div className="grid grid-cols-5 gap-2 mt-2 mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                <div>Reparto</div><div>Addetti</div><div>Età personale</div><div>Età infra (anni)</div><div>Ultimo rinnovo</div>
              </div>
              <div className="space-y-2">
                <DeptRow label="IT" value={deptIt} onChange={setDeptIt} />
                <DeptRow label="OT" value={deptOt} onChange={setDeptOt} />
                <DeptRow label="Security" value={deptSec} onChange={setDeptSec} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label className="data-label">Presenza OT / impianti industriali?</Label>
              <button data-testid="wiz-ot-toggle" onClick={() => setCompany({ ...company, ot_present: !company.ot_present })} className={`px-3 py-1 text-xs border ${company.ot_present ? "bg-primary text-white border-primary" : "border-border text-muted-foreground"}`}>{company.ot_present ? "Sì" : "No"}</button>
            </div>
            {company.ot_present && <div><Label className="data-label">Note OT (impianti, PLC, SCADA…)</Label><Textarea className="mt-1 rounded-none" rows={2} value={company.ot_notes} onChange={(e) => setCompany({ ...company, ot_notes: e.target.value })} /></div>}
            <div><Label className="data-label">Note generali</Label><Textarea className="mt-1 rounded-none" rows={2} value={company.notes} onChange={(e) => setCompany({ ...company, notes: e.target.value })} /></div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <p className="text-sm text-muted-foreground">Registra i principali tool/software presenti. Verranno creati nell'assessment Tool IT del cliente.</p>
            {tools.map((t, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <select className="col-span-3 h-9 border border-border rounded-none text-sm px-2" value={t.category} onChange={(e) => setTools(tools.map((x, j) => j === i ? { ...x, category: e.target.value } : x))}>
                  {IT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <Input className="col-span-3 rounded-none h-9" placeholder="Tool" value={t.name} data-testid={`wiz-tool-name-${i}`} onChange={(e) => setTools(tools.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                <Input className="col-span-2 rounded-none h-9" placeholder="Vendor" value={t.vendor} onChange={(e) => setTools(tools.map((x, j) => j === i ? { ...x, vendor: e.target.value } : x))} />
                <Input className="col-span-3 rounded-none h-9" placeholder="Note" value={t.notes} onChange={(e) => setTools(tools.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))} />
                <button className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center" onClick={() => setTools(tools.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-none" data-testid="wiz-add-tool" onClick={() => setTools([...tools, { category: "Altro", name: "", vendor: "", notes: "" }])}><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi tool</Button>

            <div className="mt-4 pt-4 border-t border-border">
              <Button variant="outline" size="sm" className="rounded-none" data-testid="wiz-ai-stack" onClick={suggestStack} disabled={stackBusy}>
                {stackBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Suggerisci tool mancanti (AI)
              </Button>
              {stackSuggestions && stackSuggestions.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="wiz-stack-suggestions">
                  <p className="text-xs text-muted-foreground">In base al settore <b>{company.sector || "generico"}</b>, l'AI suggerisce questi tool mancanti:</p>
                  {stackSuggestions.map((s, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 border border-border p-3 bg-[#F9FAFB]">
                      <div className="min-w-0">
                        <div className="text-sm"><span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-1.5 py-0.5 mr-2">{s.category}</span><b>{s.tool}</b>{s.vendor ? <span className="text-muted-foreground"> · {s.vendor}</span> : null}</div>
                        {s.why && <p className="text-xs text-muted-foreground mt-1">{s.why}</p>}
                      </div>
                      <Button size="sm" variant="outline" className="rounded-none shrink-0" data-testid={`wiz-add-suggestion-${i}`} onClick={() => addSuggestion(s)}><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <p className="text-sm text-muted-foreground">Valuta la maturità (1 = assente, 5 = eccellente) per ogni area di readiness AI & Sales.</p>
            {areas.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-4 border-b border-border pb-2">
                <span className="text-sm">{a.name}</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button key={v} onClick={() => setScores({ ...scores, [a.id]: v })} className={`w-8 h-8 text-sm border ${scores[a.id] === v ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-foreground"}`}>{v}</button>
                  ))}
                </div>
              </div>
            ))}
            {areas.length === 0 && <p className="text-sm text-muted-foreground">Nessuna area di readiness disponibile.</p>}
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div className="space-y-6" data-testid="wizard-step-4">
            <p className="text-sm text-muted-foreground">Rispondi ai controlli di conformità e sicurezza (NIS2, DORA, GDPR, OT).</p>
            {fwGroups.map(({ fw, items }) => items.length > 0 && (
              <div key={fw}>
                <div className="font-heading font-black tracking-tight mb-2">{fw}</div>
                <div className="space-y-2">
                  {items.map((c) => (
                    <div key={c.id} className="border border-border p-3">
                      <div className="flex items-start gap-2">
                        <p className="text-sm flex-1">{c.question}</p>
                        {(c.regulation_ref || c.regulation_why) && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button data-testid={`wiz-reg-info-${c.id}`} className="text-primary/70 hover:text-primary shrink-0 mt-0.5" title="Riferimento normativo"><Info className="w-4 h-4" /></button>
                            </PopoverTrigger>
                            <PopoverContent className="rounded-none w-80" align="end">
                              <div className="flex items-center gap-1.5 data-label text-primary mb-1"><BookMarked className="w-3.5 h-3.5" /> {c.regulation_ref || "Riferimento normativo"}</div>
                              <p className="text-sm text-muted-foreground">{c.regulation_why}</p>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                      <div className="flex gap-1 mt-2">
                        {ANSWER_OPTS.map((o) => (
                          <button key={o.v} onClick={() => setAnswers({ ...answers, [c.id]: o.v })}
                            className={`px-3 py-1 text-xs border ${answers[c.id] === o.v ? o.cls : "bg-white text-muted-foreground border-border hover:border-foreground"}`}>{o.label}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* STEP 5 */}
        {step === 5 && (
          <div className="space-y-4" data-testid="wizard-step-5">
            <div className="bg-[#00A859]/10 border border-[#00A859]/30 p-6 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto text-[#00784A]" />
              <h3 className="mt-3 font-heading font-black text-xl tracking-tight">Sopralluogo completato</h3>
              <p className="mt-1 text-sm text-muted-foreground">Dati salvati per <b>{company.name}</b>: profilo azienda, stack IT, readiness e conformità. Tutto pronto per report e roadmap.</p>
            </div>

            {/* Riepilogo esecutivo AI */}
            <div className="border border-border p-5" data-testid="wiz-exec-summary-block">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 data-label"><Sparkles className="w-3.5 h-3.5 text-primary" /> Riepilogo esecutivo (AI)</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-none" data-testid="wiz-gen-exec-summary" onClick={generateExecSummary} disabled={execBusy}>
                    {execBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} {execSummary ? "Rigenera" : "Genera riepilogo"}
                  </Button>
                  {execSummary && <Button size="sm" variant="outline" className="rounded-none" data-testid="wiz-copy-exec-summary" onClick={copyExecSummary}><Copy className="w-3.5 h-3.5 mr-1" /> Copia</Button>}
                </div>
              </div>
              {!execSummary ? (
                <p className="text-sm text-muted-foreground mt-3">Genera una sintesi professionale (stato, rischi, opportunità) + 3 interventi prioritari, pronti da incollare nella proposta e inclusi nel report PDF.</p>
              ) : (
                <div className="mt-3 space-y-3" data-testid="wiz-exec-summary-result">
                  <p className="text-sm leading-relaxed">{execSummary.summary}</p>
                  {(execSummary.priorities || []).length > 0 && (
                    <div className="space-y-2">
                      {(execSummary.priorities || []).map((p, i) => (
                        <div key={i} className="border-l-2 border-primary pl-3">
                          <div className="text-sm font-medium">{i + 1}. {p.title}</div>
                          {p.detail && <p className="text-xs text-muted-foreground">{p.detail}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-center">
              <Button className="rounded-none" data-testid="wiz-finish" onClick={finish}>Vai alla scheda cliente <ArrowRight className="w-4 h-4 ml-2" /></Button>
              {clientId && <Button variant="outline" className="rounded-none" data-testid="wiz-report-pdf" onClick={() => window.open(`${API}/clients/${clientId}/report.pdf`, "_blank")}><FileText className="w-4 h-4 mr-2" /> Report PDF</Button>}
              {assessmentId && <Button variant="outline" className="rounded-none" onClick={() => navigate(`/readiness/${assessmentId}/onepager`)}><FileText className="w-4 h-4 mr-2" /> One-Pager</Button>}
            </div>
          </div>
        )}

        {/* Nav buttons */}
        {step < 5 && (
          <div className="flex justify-between mt-8 pt-4 border-t border-border">
            <Button variant="outline" className="rounded-none" onClick={back} disabled={step === 1 || busy}><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
            <Button className="rounded-none" data-testid="wiz-next" onClick={next} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {step === 4 ? "Completa" : "Avanti"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
